import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import { CalendarDays, Clock, CheckCircle2, ArrowRight, Send } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { api, COMPANY } from "@/lib/api";
import { APPOINTMENT_TYPES, TIME_SLOTS, TIME_SLOT_LABELS, toDateStr } from "@/data/appointments";

const inputCls = "w-full bg-transparent border border-slate-200 focus:border-primary-brand outline-none px-4 py-3 text-sm text-slate-900";

const initialForm = { name: "", email: "", phone: "", appointment_type: "pickup", reference: "", notes: "" };

export default function BookAppointment() {
  const [date, setDate] = useState(() => new Date());
  const [slot, setSlot] = useState("");
  const [booked, setBooked] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const dateStr = date ? toDateStr(date) : "";
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const loadSlots = React.useCallback(async (ds) => {
    try {
      const { data } = await api.get(`/appointments/slots?date=${ds}`);
      setBooked(data.booked || []);
    } catch (error) {
      console.error("Slot load failed:", error);
      setBooked([]);
    }
  }, []);

  useEffect(() => {
    if (!dateStr) return;
    setSlot("");
    loadSlots(dateStr);
  }, [dateStr, loadSlots]);

  const submit = async (e) => {
    e.preventDefault();
    if (!dateStr || !slot) { toast.error("Please pick a date and a time slot."); return; }
    if (!form.name || !form.email || !form.phone) { toast.error("Please fill your name, email, and phone."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/appointments", { ...form, date: dateStr, time_slot: slot });
      setConfirmed(data);
    } catch (err) {
      console.error("Booking failed:", err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Booking failed. Please try again.");
      if (err?.response?.status === 409) {
        loadSlots(dateStr);
        setSlot("");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <div data-testid="book-page" className="min-h-screen bg-brand-bg pt-24 pb-24">
        <div className="max-w-2xl mx-auto px-6">
          <div data-testid="book-success" className="border border-slate-200 bg-white p-10 lg:p-14 text-center">
            <CheckCircle2 className="h-14 w-14 text-primary-brand mx-auto" />
            <h1 className="font-heading text-3xl md:text-4xl font-black mt-5 text-slate-900">Appointment Booked!</h1>
            <div className="mt-6 inline-block text-left border border-slate-200 bg-brand-bg p-5 text-sm text-slate-700 space-y-1">
              <div><span className="font-bold text-slate-900">{confirmed.name}</span> · {confirmed.email}</div>
              <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-primary-brand" /> {confirmed.date}</div>
              <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-primary-brand" /> {TIME_SLOT_LABELS[confirmed.time_slot]}</div>
              <div className="uppercase tracking-wider text-xs font-bold text-primary-brand">{confirmed.appointment_type.replace(/_/g, " ")}</div>
            </div>
            <p className="text-slate-600 mt-6 text-sm">
              We'll confirm by email or phone. Need to change it? Call {COMPANY.phone}.
            </p>
            <button
              onClick={() => { setConfirmed(null); setForm(initialForm); setSlot(""); loadSlots(dateStr); }}
              data-testid="book-another"
              className="mt-8 inline-flex items-center gap-2 border border-primary-brand text-primary-brand px-6 py-3 uppercase tracking-wider text-sm font-bold hover:bg-primary-brand hover:text-white transition-colors"
            >
              Book another <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="book-page" className="min-h-screen bg-brand-bg pt-24 pb-24">
      <Toaster position="top-right" richColors />
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="text-xs tracking-[0.3em] uppercase font-bold text-primary-brand mb-3">Appointments</div>
          <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-slate-900">
            Book a date & time with us.
          </h1>
          <p className="mt-3 text-slate-600 max-w-2xl">
            Schedule a freight pickup, delivery window, business consultation, or job interview.
            Hours: Mon–Fri 7:00 AM – 6:00 PM.
          </p>
        </motion.div>

        <form onSubmit={submit} className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Date + time */}
          <div className="border border-slate-200 bg-white p-6">
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-primary-brand mb-4 flex items-center gap-2">
              <CalendarDays className="h-4 w-4" /> 1. Pick a date
            </div>
            <div data-testid="book-calendar" className="flex justify-center">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(d) => d && setDate(d)}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
              />
            </div>
            <div className="mt-6 text-xs uppercase tracking-[0.25em] font-bold text-primary-brand mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4" /> 2. Pick a time — {dateStr}
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {TIME_SLOTS.map((s) => {
                const taken = booked.includes(s.key);
                return (
                  <button
                    key={s.key}
                    type="button"
                    disabled={taken}
                    onClick={() => setSlot(s.key)}
                    data-testid={`slot-${s.key}`}
                    className={`px-2 py-2.5 text-xs font-bold uppercase tracking-wider border transition-colors ${
                      taken
                        ? "border-slate-100 text-slate-300 line-through cursor-not-allowed"
                        : slot === s.key
                          ? "bg-primary-brand border-primary-brand text-white"
                          : "border-slate-200 text-slate-700 hover:border-primary-brand hover:text-primary-brand"
                    }`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Details */}
          <div className="border border-slate-200 bg-white p-6 flex flex-col">
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-primary-brand mb-4">3. Your details</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="md:col-span-2">
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-2">Appointment type *</div>
                <select value={form.appointment_type} onChange={upd("appointment_type")} className={inputCls} data-testid="book-type">
                  {APPOINTMENT_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </label>
              <label>
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-2">Full name *</div>
                <input required value={form.name} onChange={upd("name")} className={inputCls} data-testid="book-name" />
              </label>
              <label>
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-2">Email *</div>
                <input type="email" required value={form.email} onChange={upd("email")} className={inputCls} data-testid="book-email" />
              </label>
              <label>
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-2">Phone *</div>
                <input required value={form.phone} onChange={upd("phone")} className={inputCls} data-testid="book-phone" />
              </label>
              <label>
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-2">Tracking # / Quote ref (optional)</div>
                <input value={form.reference} onChange={upd("reference")} placeholder="e.g. LPL1234ABCD" className={inputCls} data-testid="book-reference" />
              </label>
              <label className="md:col-span-2">
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-2">Notes</div>
                <textarea rows={4} value={form.notes} onChange={upd("notes")} className={inputCls + " resize-none"} data-testid="book-notes" placeholder="Anything we should know — dock details, load size, interview position…" />
              </label>
            </div>
            <div className="mt-auto pt-6 flex items-center justify-between gap-4">
              <div className="text-xs text-slate-500">
                {slot ? `Selected: ${dateStr} at ${TIME_SLOT_LABELS[slot]}` : "Pick a date and time on the left."}
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="book-submit"
                className="inline-flex items-center gap-2 bg-primary-brand hover:bg-[#E04F00] disabled:opacity-60 text-white px-8 py-4 font-bold uppercase tracking-wider text-sm"
              >
                {submitting ? "Booking…" : "Book Appointment"} <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
